import { CommonModule, DatePipe } from '@angular/common';
import {
  Component,
  effect,
  inject,
  input,
  signal,
  OnInit,
} from '@angular/core';
import {
  Assignment,
  AssignmentMontage,
  AssignmentService,
} from '@api/models/assignment.model';
import { AssignmentsService } from '@api/services/assignments.service';
import { CardComponent } from '@ui/card/card.component';
import { IconComponent } from '@ui/icon/icon.component';
import { ModalService } from '@ui/modal/modal.service';
import { finalize } from 'rxjs';
import { DossierModalComponent } from '../dossier-modal/dossier-modal.component';
import { AssignmentsPriceRulesComponent } from '../assignments-price-rules/assignments-price-rules.component';

interface Document {
  subject_id: string;
  subject: string;
  type: 'aanvraag' | 'offerte' | 'technisch';
  created_at: string;
}

@Component({
  selector: 'app-assignments-montage',
  standalone: true,
  imports: [
    CommonModule,
    CardComponent,
    DatePipe,
    IconComponent,
    AssignmentsPriceRulesComponent,
  ],
  template: `
    <ui-card [title]="'Rail'" [loading]="loading()">
      @for (montage of montages(); track montage.project.id; let isLast = $last) {
      <div
        class="flex flex-col gap-3 relative"
        [class.border-b]="!isLast"
        [class.border-gray-200]="!isLast"
        [class.pb-4]="!isLast"
        [class.mb-4]="!isLast"
      >
        <div class="absolute top-0 right-0">
          <span [class]="getStatusClass(montage.project.status)">
            {{montage.project.status | titlecase}}
          </span>
        </div>
        <!-- <div class="flex justify-end">
          <span [class]="getStatusClass(montage.project.status)">
            {{montage.project.status | titlecase}}
          </span>
        </div> -->
        <div class="grid grid-cols-3 gap-y-3 text-sm">
          <div class="grid grid-cols-[150px_1fr] gap-y-3 text-sm">
            <span class="text-gray-600">Rail type:</span>
            <span class="font-medium">
              {{ montage.project_details.description }}
            </span>
            <span class="text-gray-600">Rail fabrikant:</span>
            <span class="font-medium">
              {{ montage.project_details.manufacturer }}
            </span>
            <span class="text-gray-600">Gewenste leverdatum:</span>
            <span class="font-medium">{{
              montage.calculation?.gewenste_leverdatum | date : 'dd-MM-yyyy'
            }}</span>

            <span class="text-gray-600">Definitieve leverdatum:</span>
            <span class="font-medium">{{
              montage.calculation?.definitieve_leverdatum | date : 'dd-MM-yyyy'
            }}</span>
          </div>

          @if (getMontageBon(montage) || montage.calculation?.traplift_geplaatst) {
          <div class="grid grid-cols-[150px_1fr] gap-y-3 text-sm">
            <span class="text-gray-600">Datum plaatsing:</span>
            <span class="font-medium">{{
              montage.calculation.plaatsingsdatum | date : 'dd-MM-yyyy'
            }}</span>
            <span class="text-gray-600">Installatie gelukt:</span>
            <span class="font-medium">{{
              montage.calculation.traplift_geplaatst ? 'Ja' : 'Nee'
            }}</span>
            <span class="text-gray-600">Nazorg nodig:</span>
            <span class="font-medium">{{
              getMontageBon(montage)?.nazorg_nodig ? 'Ja' : 'Nee'
            }}</span>

            <span class="text-gray-600">&nbsp;</span>
            <span class="font-medium">&nbsp;</span>
          </div>
          }

          <div class="text-sm flex flex-col gap-y-3">
            @for (item of montage.installation_records; track item.subject_id) {

            <div
              class="flex items-center gap-3 cursor-pointer hover:bg-gray-50 rounded"
              (click)="openDossierModal(item)"
            >
              <ui-icon [name]="'document-text'" [color]="'secondary'"></ui-icon>
              <div>
                <div class="text-sm font-medium">{{ item.subject }}</div>
              </div>
            </div>
            }
          </div>
        </div>
        <div class="grid grid-cols-[120px_1fr] gap-y-3 text-sm">
          <span class="text-gray-600">Opmerking:</span>
          <span class="font-medium">{{
            getMontageBon(montage)?.opmerking
          }}</span>
        </div>
      </div>
      }

      <app-assignments-price-rules
        [assignment]="assignment()"
        [embedded]="true"
      ></app-assignments-price-rules>
    </ui-card>
  `,
  styles: `
    :host {
      display: block;
    }
  `,
})
export class AssignmentsMontageComponent {
  assignmentsService = inject(AssignmentsService);
  modalService = inject(ModalService);
  assignment = input.required<Assignment | null>();

  montages = signal<AssignmentMontage[] | null>(null);
  service = signal<AssignmentService | null>(null);
  loading = signal<boolean>(false);

  constructor() {
    effect(
      () => {
        let assignmentId = this.assignment()?.id;
        if (assignmentId) {
          this.getMontages(assignmentId);
        }
      },
      { allowSignalWrites: true }
    );
  }

  getMontages(assignmentId: string) {
    this.loading.set(true);
    this.assignmentsService
      .getAssignmentMontages(assignmentId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe((montages) => {
        this.montages.set(montages);
      });
  }

  openDossierModal(item: any) {
    let modal = this.modalService.open(DossierModalComponent, {
      data: {
        item,
        assignmentId: this.assignment()?.id,
      },
    });

    modal.onClose.subscribe(() => {
      console.log('modal closed');
    });
  }

  getMontageBon(montage: AssignmentMontage) {
    return montage.installation_records.find((record) => record.type_id === 64);
  }

  getStatusClass(status: string): string {
    const baseClasses = 'px-2.5 py-0.5 rounded-full text-xs font-medium';
    switch (status.toLowerCase()) {
      case 'actief':
        return `bg-green-100 text-green-800 ${baseClasses}`;
      case 'realisatie':
        return `bg-yellow-100 text-yellow-800 ${baseClasses}`;
      default: // inactief
        return `bg-gray-100 text-gray-800 ${baseClasses}`;
    }
  }

  // getMontageService() {
  //   return this.montage()?.installation_records;
  // }
}
